'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const STORAGE_KEY = 'scroll-positions';
const MAX_ENTRIES = 50;
const OBSERVER_TIMEOUT_MS = 5000;

function getPositions(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function savePosition(pathname: string, scrollTop: number) {
  const positions = getPositions();
  positions[pathname] = scrollTop;

  const keys = Object.keys(positions);
  if (keys.length > MAX_ENTRIES) {
    for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) {
      delete positions[k];
    }
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch { /* quota exceeded — non-critical */ }
}

export function ScrollRestoration() {
  const pathname = usePathname();
  const prevPathRef = useRef(pathname);
  const isPopstateRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  // Track popstate (back/forward navigation)
  useEffect(() => {
    const onPopstate = () => { isPopstateRef.current = true; };
    window.addEventListener('popstate', onPopstate);
    return () => window.removeEventListener('popstate', onPopstate);
  }, []);

  // Continuously save scroll position so we have an accurate value
  // BEFORE the page content changes (useEffect fires too late)
  useEffect(() => {
    const el = document.getElementById('main-scroll');
    if (!el) return;
    const onScroll = () => {
      lastScrollTopRef.current = el.scrollTop;
    };
    // Capture initial position
    lastScrollTopRef.current = el.scrollTop;
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = document.getElementById('main-scroll');
    if (!el) return;

    // Save the PREVIOUS page's scroll position using the continuously-tracked value
    if (prevPathRef.current !== pathname) {
      savePosition(prevPathRef.current, lastScrollTopRef.current);
    }

    if (isPopstateRef.current) {
      isPopstateRef.current = false;
      const saved = getPositions()[pathname];
      if (saved != null && saved > 0) {
        const tryRestore = () => {
          if (el.scrollHeight > saved) {
            el.scrollTop = saved;
            return true;
          }
          return false;
        };

        if (!tryRestore()) {
          const observer = new MutationObserver(() => {
            if (tryRestore()) observer.disconnect();
          });
          observer.observe(el, { childList: true, subtree: true });
          const timeout = setTimeout(() => observer.disconnect(), OBSERVER_TIMEOUT_MS);
          prevPathRef.current = pathname;
          return () => {
            observer.disconnect();
            clearTimeout(timeout);
          };
        }
      }
    } else if (prevPathRef.current !== pathname) {
      el.scrollTop = 0;
    }

    prevPathRef.current = pathname;
  }, [pathname]);

  return null;
}
